/**
 * S60 — People Memory Pipeline Dashboard
 * ---------------------------------------------------------------------------
 * Visão agregada do funil de extração → promoção:
 *   - Contagens por status (candidate / awaiting_confirmation / confirmed / rejected)
 *   - Breakdown por motivo de promoção (a/b/c/d) lido de metadata.promotion_reason
 *   - Filtros: grupo (opcional, "todos") + período (24h, 7d, 30d, 90d)
 *
 * Os dados vêm direto de syra_person_memories — sem RPC, sem agregação no servidor.
 */
import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle2, Clock, FileQuestion, XCircle, Users, ShieldCheck, CalendarClock, UserCheck, ChevronLeft, ChevronRight } from "lucide-react";

const PERIODS = [
  { value: "24h", label: "Últimas 24h", hours: 24 },
  { value: "7d", label: "Últimos 7 dias", hours: 24 * 7 },
  { value: "30d", label: "Últimos 30 dias", hours: 24 * 30 },
  { value: "90d", label: "Últimos 90 dias", hours: 24 * 90 },
  { value: "all", label: "Todo o histórico", hours: 0 },
];

const REASON_META: Record<string, { label: string; description: string; icon: any; color: string }> = {
  a_corroboration: {
    label: "(a) Corroboração ≥3 autores",
    description: "Três ou mais membros distintos afirmaram o mesmo fato.",
    icon: Users,
    color: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30",
  },
  b_admin: {
    label: "(b) Promoção administrativa",
    description: "Admin marcou explicitamente promote_now.",
    icon: ShieldCheck,
    color: "bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30",
  },
  c_objective_recent: {
    label: "(c) Objetivo + recente (≥2/7d)",
    description: "Atributo objetivo com ao menos 2 ocorrências em 7 dias.",
    icon: CalendarClock,
    color: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
  },
  d_self_claim_objective: {
    label: "(d) Auto-declaração objetiva",
    description: "Próprio sujeito declarou um fato objetivo sobre si.",
    icon: UserCheck,
    color: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  },
};

const STATUS_META: Record<string, { label: string; icon: any; tone: string }> = {
  candidate: { label: "Candidate", icon: FileQuestion, tone: "text-muted-foreground" },
  awaiting_confirmation: { label: "Awaiting confirmation", icon: Clock, tone: "text-amber-600 dark:text-amber-400" },
  confirmed_by_subject: { label: "Confirmed by subject", icon: CheckCircle2, tone: "text-emerald-600 dark:text-emerald-400" },
  confirmed_by_admin: { label: "Confirmed by admin", icon: CheckCircle2, tone: "text-blue-600 dark:text-blue-400" },
  rejected: { label: "Rejected", icon: XCircle, tone: "text-rose-600 dark:text-rose-400" },
};

interface Props {
  groupId: string;
  onGroupChange: (id: string) => void;
  groups: { id: string; name: string }[];
  period: string;
  onPeriodChange: (p: string) => void;
}

export function PeopleMemoryPipelineDashboard({ groupId, onGroupChange, groups, period, onPeriodChange }: Props) {
  const sinceIso = useMemo(() => {
    const p = PERIODS.find((p) => p.value === period);
    if (!p || p.hours === 0) return null;
    return new Date(Date.now() - p.hours * 3600 * 1000).toISOString();
  }, [period]);

  const { data, isLoading } = useQuery({
    queryKey: ["pm-pipeline-dashboard", groupId || "all", period],
    refetchInterval: 30_000,
    queryFn: async () => {
      let q = supabase
        .from("syra_person_memories")
        .select("id, confirmation_status, metadata, group_id, attribute, created_at, updated_at")
        .limit(5000);
      if (groupId) q = q.eq("group_id", groupId);
      if (sinceIso) q = q.gte("created_at", sinceIso);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

  const summary = useMemo(() => {
    const byStatus: Record<string, number> = {};
    const byReason: Record<string, number> = { a_corroboration: 0, b_admin: 0, c_objective_recent: 0, d_self_claim_objective: 0, unknown: 0 };
    const byAttribute: Record<string, number> = {};

    for (const row of data ?? []) {
      const st = row.confirmation_status as string;
      byStatus[st] = (byStatus[st] ?? 0) + 1;
      byAttribute[row.attribute] = (byAttribute[row.attribute] ?? 0) + 1;

      const promotedStatuses = ["awaiting_confirmation", "confirmed_by_subject", "confirmed_by_admin"];
      if (promotedStatuses.includes(st)) {
        const reason = (row.metadata as any)?.promotion_reason as string | undefined;
        if (reason && byReason[reason] !== undefined) byReason[reason]++;
        else byReason.unknown++;
      }
    }
    const topAttributes = Object.entries(byAttribute).sort((a, b) => b[1] - a[1]).slice(0, 8);
    return { byStatus, byReason, topAttributes, total: data?.length ?? 0 };
  }, [data]);

  const groupNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of groups) m.set(g.id, g.name);
    return m;
  }, [groups]);

  const promotions = useMemo(() => {
    const promotedStatuses = new Set(["awaiting_confirmation", "confirmed_by_subject", "confirmed_by_admin"]);
    return (data ?? [])
      .filter((row) => promotedStatuses.has(row.confirmation_status as string))
      .map((row) => {
        const meta = (row.metadata as any) ?? {};
        const reason = (meta.promotion_reason as string | undefined) ?? "unknown";
        const promotedAt = (meta.promoted_at as string | undefined) ?? row.updated_at ?? row.created_at;
        return {
          id: row.id,
          attribute: row.attribute,
          group_id: row.group_id as string,
          reason,
          promoted_at: promotedAt,
        };
      })
      .sort((a, b) => new Date(b.promoted_at).getTime() - new Date(a.promoted_at).getTime());
  }, [data]);

  const PAGE_SIZE = 20;
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [groupId, period]);
  const totalPages = Math.max(1, Math.ceil(promotions.length / PAGE_SIZE));
  const pageRows = promotions.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Grupo</label>
            <Select value={groupId || "__all__"} onValueChange={(v) => onGroupChange(v === "__all__" ? "" : v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os grupos</SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Período</label>
            <Select value={period} onValueChange={onPeriodChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIODS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Status counts */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Funil por status</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(STATUS_META).map(([status, meta]) => {
            const Icon = meta.icon;
            const count = summary.byStatus[status] ?? 0;
            return (
              <Card key={status}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <Icon className={`w-5 h-5 ${meta.tone}`} />
                    <span className={`text-3xl font-bold ${meta.tone}`}>
                      {isLoading ? "…" : count}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{meta.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Total na janela: <strong>{isLoading ? "…" : summary.total}</strong>
        </p>
      </div>

      {/* Promotion reasons */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Motivo de promoção</h2>
        <p className="text-xs text-muted-foreground mb-3">
          Conta apenas registros já promovidos (awaiting_confirmation ou confirmed). Lido de <code>metadata.promotion_reason</code>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(REASON_META).map(([reason, meta]) => {
            const Icon = meta.icon;
            const count = summary.byReason[reason] ?? 0;
            return (
              <Card key={reason} className={`border ${meta.color}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      {meta.label}
                    </span>
                    <Badge variant="outline" className="text-base font-bold">
                      {isLoading ? "…" : count}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-xs text-muted-foreground">{meta.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
        {summary.byReason.unknown > 0 && (
          <p className="text-xs text-muted-foreground mt-2">
            <strong>{summary.byReason.unknown}</strong> promovidos sem reason registrado (anteriores ao S60).
          </p>
        )}
      </div>

      {/* Top attributes */}
      {summary.topAttributes.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3">Top atributos extraídos</h2>
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-2">
                {summary.topAttributes.map(([attr, count]) => (
                  <Badge key={attr} variant="secondary" className="text-sm">
                    {attr} <span className="ml-2 font-bold">{count}</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent promotions table */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Últimas promoções</h2>
        <p className="text-xs text-muted-foreground mb-3">
          Lista de registros promovidos no período selecionado, ordenados do mais recente para o mais antigo.
        </p>
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quando</TableHead>
                  <TableHead>Grupo</TableHead>
                  <TableHead>Atributo</TableHead>
                  <TableHead>Motivo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Carregando…</TableCell></TableRow>
                ) : pageRows.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Nenhuma promoção no período.</TableCell></TableRow>
                ) : pageRows.map((p) => {
                  const reasonMeta = REASON_META[p.reason];
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {new Date(p.promoted_at).toLocaleString("pt-BR")}
                      </TableCell>
                      <TableCell className="text-sm">
                        {groupNameById.get(p.group_id) ?? <span className="text-muted-foreground font-mono text-xs">{p.group_id.slice(0, 8)}…</span>}
                      </TableCell>
                      <TableCell className="text-sm font-medium">{p.attribute}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={reasonMeta?.color ?? "text-muted-foreground"}>
                          {reasonMeta?.label ?? p.reason}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        {promotions.length > 0 && (
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-muted-foreground">
              Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, promotions.length)} de {promotions.length}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                <ChevronLeft className="w-4 h-4" /> Anterior
              </Button>
              <span className="text-xs text-muted-foreground">Página {page + 1} de {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}>
                Próxima <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
